import { Paragraph, SentenceObject } from "../types";

// Keep in sync with realignWords in api/src/modules/short-video/utils/realignWords.ts
// so the preview shows the same word timing the API saves.

// Shortest a word may be when squeezed into a pause; below this an inserted
// word borrows time from its neighbour instead.
const MIN_WORD_DURATION = 0.08;

interface Timing {
  start: number;
  end: number;
}

const normalize = (word: string) =>
  word.toLowerCase().replace(/[^\p{L}\p{N}']/gu, "");

const spread = (count: number, start: number, end: number): Timing[] => {
  const step = (end - start) / count;
  return Array.from({ length: count }, (_, i) => ({
    start: start + i * step,
    end: i === count - 1 ? end : start + (i + 1) * step,
  }));
};

const hasTiming = (
  words: SentenceObject[],
): words is (SentenceObject & Timing)[] =>
  words.length > 0 && words.every((w) => w.start != null && w.end != null);

/**
 * Keeps preview subtitle events in sync when paragraph text is edited.
 * Words that survived the edit keep their original timing, a word replaced
 * one-for-one takes the timing of the word it replaced, and only inserted or
 * merged words get time spread across the span of what they replaced, so
 * word-level caption animations stay in sync with the audio.
 */
export function syncParagraphWordsFromText(paragraph: Paragraph): Paragraph {
  const tokens = paragraph.text.trim()
    ? paragraph.text.trim().split(/\s+/)
    : [];

  if (tokens.length === 0) {
    return { ...paragraph, words: [] };
  }

  const oldWords = paragraph.words;
  if (!hasTiming(oldWords)) {
    const start = oldWords[0]?.start ?? paragraph.start ?? null;
    const end = oldWords[oldWords.length - 1]?.end ?? paragraph.end ?? null;
    if (start == null || end == null) {
      return {
        ...paragraph,
        words: tokens.map((word) => ({ word, start: null, end: null })),
      };
    }
    return {
      ...paragraph,
      words: spread(tokens.length, start, end).map((t, i) => ({
        word: tokens[i],
        ...t,
      })),
    };
  }

  // Longest common subsequence of normalized words gives the anchors: words
  // the edit left untouched.
  const n = oldWords.length;
  const m = tokens.length;
  const oldKeys = oldWords.map((w) => normalize(w.word));
  const newKeys = tokens.map(normalize);
  const lcs = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0),
  );
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      lcs[i][j] =
        oldKeys[i] && oldKeys[i] === newKeys[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const anchors: [number, number][] = [];
  for (let i = 0, j = 0; i < n && j < m; ) {
    if (oldKeys[i] && oldKeys[i] === newKeys[j]) {
      anchors.push([i, j]);
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      i += 1;
    } else {
      j += 1;
    }
  }

  const result: SentenceObject[] = tokens.map((word) => ({
    word,
    start: 0,
    end: 0,
  }));
  const setTiming = (index: number, timing: Timing) => {
    result[index] = {
      word: result[index].word,
      start: timing.start,
      end: timing.end,
    };
  };
  anchors.forEach(([o, t]) => setTiming(t, oldWords[o]));

  let prevOld = -1;
  let prevNew = -1;
  [...anchors, [n, m] as [number, number]].forEach(([nextOld, nextNew]) => {
    const removed = oldWords.slice(prevOld + 1, nextOld);
    const added = nextNew - prevNew - 1;

    if (added > 0 && removed.length === added) {
      removed.forEach((w, k) => setTiming(prevNew + 1 + k, w));
    } else if (added > 0 && removed.length > 0) {
      spread(added, removed[0].start, removed[removed.length - 1].end).forEach(
        (t, k) => setTiming(prevNew + 1 + k, t),
      );
    } else if (added > 0) {
      // Pure insertion: use the pause between the neighbours if it is long
      // enough, otherwise share a neighbour's time with it.
      const gapStart = prevOld >= 0 ? oldWords[prevOld].end : oldWords[0].start;
      const gapEnd =
        nextOld < n ? oldWords[nextOld].start : oldWords[n - 1].end;
      if (gapEnd - gapStart >= MIN_WORD_DURATION * added) {
        spread(added, gapStart, gapEnd).forEach((t, k) =>
          setTiming(prevNew + 1 + k, t),
        );
      } else if (prevNew >= 0) {
        spread(added + 1, oldWords[prevOld].start, gapEnd).forEach((t, k) =>
          setTiming(prevNew + k, t),
        );
      } else {
        spread(added + 1, gapStart, oldWords[nextOld].end).forEach((t, k) =>
          setTiming(k, t),
        );
      }
    }

    prevOld = nextOld;
    prevNew = nextNew;
  });

  return { ...paragraph, words: result };
}
